import { AlertCircle, MessageSquare, RefreshCw, Upload } from "lucide-react";
import React from "react";
import { getApiErrorMessage, portalApi } from "../api/client";
import type { ApiEnvelope } from "../api/types";
import { FileRow } from "./FileRow";
import { UploadRequirementModal } from "./UploadRequirementModal";
import type {
  Requirement,
  RequirementDetail as RequirementDetailData,
  RequirementFile,
} from "../types/compliance";
import { Button, RequirementTimelineDialog } from "../components/ui";

export function RequirementDetail({
  requirement,
  onChanged,
}: {
  requirement: Requirement;
  onChanged?(): Promise<void> | void;
}) {
  const [detail, setDetail] = React.useState<RequirementDetailData | null>(
    null
  );
  const [loading, setLoading] = React.useState(true);
  const [forbidden, setForbidden] = React.useState(false);
  const [error, setError] = React.useState("");
  const [busyFile, setBusyFile] = React.useState<number | null>(null);
  const [fileError, setFileError] = React.useState("");
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [timelineOpen, setTimelineOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    setForbidden(false);
    try {
      const response = await portalApi.get<ApiEnvelope<RequirementDetailData>>(
        `/portal/requirements/${requirement.cdreq}`
      );
      setDetail(response.data.data);
    } catch (requestError: any) {
      if (requestError.response?.status === 403) setForbidden(true);
      else {
        setError(
          getApiErrorMessage(
            requestError,
            "Não foi possível carregar os detalhes."
          )
        );
      }
    } finally {
      setLoading(false);
    }
  }, [requirement.cdreq]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function openFile(file: RequirementFile, download: boolean) {
    setBusyFile(file.cdreqfile);
    setFileError("");
    try {
      const response = await portalApi.get<Blob>(
        `/portal/requirements/${requirement.cdreq}/files/${file.cdreqfile}`,
        {
          params: download ? { download: 1 } : undefined,
          responseType: "blob",
        }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      if (download) link.download = file.filename;
      else {
        link.target = "_blank";
        link.rel = "noopener noreferrer";
      }
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (requestError) {
      setFileError(
        getApiErrorMessage(requestError, "Não foi possível abrir o arquivo.")
      );
    } finally {
      setBusyFile(null);
    }
  }

  if (loading) {
    return (
      <div className="space-y-3 border-t border-slate-200 bg-slate-50 p-4">
        <div className="h-16 animate-pulse rounded-xl bg-slate-200" />
        <div className="h-16 animate-pulse rounded-xl bg-slate-200" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="border-t border-slate-200 bg-slate-50 p-4">
        <div className="portal-message">
          <AlertCircle size={18} />
          <div>
            <strong>Sem permissão</strong>
            <p>Seu perfil não pode visualizar arquivos e histórico.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="border-t border-slate-200 bg-slate-50 p-4">
        <div className="portal-message portal-message-error">
          <AlertCircle size={18} />
          <div className="flex-1">
            <strong>Falha ao carregar</strong>
            <p>{error || "O detalhe não está disponível."}</p>
            <Button
              type="button"
              variant="link"
              className="mt-2 h-auto p-0"
              onClick={() => void load()}
            >
              <RefreshCw size={14} />
              Tentar novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentApproved = detail.files.some(
    (file) => file.fgcurrent === 1 && file.fgstatus === 2
  );
  const replacementBlocked =
    detail.fgqtfile === 1 &&
    detail.fgallowreplace === 2 &&
    currentApproved &&
    (detail.idvalidity === "valid" || !detail.dtvalidity);
  const blockedReason = replacementBlocked
    ? "Existe um documento aprovado e vigente. A substituição foi bloqueada pela empresa compradora."
    : undefined;
  const hasCurrent = detail.files.some((file) => file.fgcurrent === 1);

  return (
    <div className="space-y-6 border-t border-slate-200 bg-slate-50 p-4">
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h4 className="text-sm font-semibold text-slate-900">Arquivos</h4>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => setTimelineOpen(true)}>
              <MessageSquare size={16} />
              Ver timeline
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={replacementBlocked}
              onClick={() => setUploadOpen(true)}
              title={blockedReason}
            >
              <Upload size={16} />
              {detail.fgqtfile === 1 && hasCurrent
                ? "Substituir"
                : detail.fgqtfile === 1
                ? "Enviar"
                : "Adicionar arquivo"}
            </Button>
          </div>
        </div>
        {blockedReason && (
          <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {blockedReason}
          </p>
        )}
        {fileError && (
          <p role="alert" className="mb-3 text-sm text-red-700">
            {fileError}
          </p>
        )}
        {detail.files.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600">
            Nenhum arquivo foi enviado. Este documento continua disponível para
            envio.
          </p>
        ) : (
          <div className="space-y-2">
            {detail.files.map((file) => (
              <FileRow
                key={file.cdreqfile}
                file={file}
                busy={busyFile === file.cdreqfile}
                onOpen={(download) => void openFile(file, download)}
              />
            ))}
          </div>
        )}
      </section>

      <RequirementTimelineDialog
        open={timelineOpen}
        events={detail.timeline}
        onOpenChange={setTimelineOpen}
      />
      <UploadRequirementModal
        open={uploadOpen}
        requirement={detail}
        blockedReason={blockedReason}
        onClose={() => setUploadOpen(false)}
        onUploaded={async () => {
          await load();
          await onChanged?.();
        }}
      />
    </div>
  );
}
